"use client";

/**
 * Organization Switcher Component
 *
 * Provides UI for switching between organizations.
 * Uses Supabase Auth for organization management.
 *
 * Architecture Rule: AU-01 - Auth Abstraction Layer
 */
import { Building2, Check, ChevronDown, FlaskConical, Plus, Settings } from "lucide-react";
import Image from "next/image";
import { useRouter } from "next/navigation";

import { useOrganizationSwitcher } from "@/hooks/auth";
import { useOrganizationContext } from "@/hooks/organization/use-organization-context";
import { usePatientsStore } from "@/lib/stores/patients";
import { cn } from "@/lib/utils";
import type { OrganizationMetadata } from "@/types/user-roles";

import { Badge } from "./ui/badge";
import { Button } from "./ui/button";
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuSeparator,
  DropdownMenuTrigger,
} from "./ui/dropdown-menu";
import { Skeleton } from "./ui/skeleton";

interface OrganizationSwitcherProps {
  variant?: "default" | "compact";
  showCreateOption?: boolean;
  showManageOption?: boolean;
}

/** Gradient used for org icon badges — varies between demo (amber) and prod (blue). */
function gradientForOrg(isDemo: boolean) {
  return isDemo ? "from-[hsl(36_95%_50%)] to-[hsl(25_92%_50%)]" : "from-[hsl(217_91%_60%)] to-[hsl(225_78%_46%)]";
}

export function OrganizationSwitcher({
  variant = "default",
  showCreateOption = false,
  showManageOption = true,
}: OrganizationSwitcherProps) {
  const router = useRouter();
  const { userMemberships, setActive, isLoaded: switcherLoaded } = useOrganizationSwitcher();
  const { orgId, orgName, isDemoOrg, isLyfeAdmin, isLoaded: contextLoaded } = useOrganizationContext();
  const { clearPatients } = usePatientsStore();

  const isLoaded = switcherLoaded && contextLoaded;

  if (!isLoaded) {
    return variant === "compact" ? (
      <Skeleton className="h-9 w-9 rounded-lg" />
    ) : (
      <Skeleton className="h-10 w-48 rounded-md" />
    );
  }

  const memberships = userMemberships;

  const handleSwitchOrg = async (targetOrgId: string) => {
    const membership = memberships.find((m: { organization: { id: string } }) => m.organization.id === targetOrgId);
    if (membership) {
      clearPatients();
      await setActive?.({ organization: membership.organization.id });
      router.push("/patients");
    }
  };

  const handleManageOrg = () => {
    router.push("/admin/organizations");
  };

  const handleCreateOrg = () => {
    router.push("/admin/organizations/new");
  };

  // Compact variant — sidebar icon button
  if (variant === "compact") {
    return (
      <DropdownMenu>
        <DropdownMenuTrigger asChild>
          <Button
            variant="ghost"
            size="icon"
            className="group/orgtrig relative h-9 w-9 rounded-lg hover:bg-sidebar-accent"
            title={orgName || "Select Organization"}
          >
            <span
              className={cn(
                "flex h-7 w-7 items-center justify-center rounded-md bg-gradient-to-br text-white shadow-sm ring-1 ring-white/10 transition-transform duration-200 ease-out-expo group-hover/orgtrig:scale-105",
                gradientForOrg(isDemoOrg)
              )}
            >
              <Building2 className="h-3.5 w-3.5 drop-shadow-sm" />
            </span>
            {isDemoOrg && (
              <span className="absolute -right-0.5 -top-0.5 flex h-2.5 w-2.5">
                <span className="absolute inset-0 animate-ping rounded-full bg-healthcare-warning/60" />
                <span className="relative h-2.5 w-2.5 rounded-full bg-healthcare-warning ring-2 ring-sidebar" />
              </span>
            )}
          </Button>
        </DropdownMenuTrigger>
        <DropdownMenuContent align="start" sideOffset={10} className="w-72 p-0">
          <OrganizationMenuContent
            currentOrgId={orgId}
            currentOrgName={orgName}
            currentIsDemo={isDemoOrg}
            memberships={memberships}
            isLyfeAdmin={isLyfeAdmin}
            showCreateOption={showCreateOption}
            showManageOption={showManageOption}
            onSwitchOrg={handleSwitchOrg}
            onManageOrg={handleManageOrg}
            onCreateOrg={handleCreateOrg}
          />
        </DropdownMenuContent>
      </DropdownMenu>
    );
  }

  // Default variant — full button with org name
  return (
    <DropdownMenu>
      <DropdownMenuTrigger asChild>
        <Button variant="outline" className="group/orgbtn justify-between gap-2 px-2.5 hover:bg-accent/60">
          <div className="flex min-w-0 items-center gap-2">
            <span
              className={cn(
                "flex h-6 w-6 shrink-0 items-center justify-center rounded-md bg-gradient-to-br text-white shadow-xs ring-1 ring-white/10",
                gradientForOrg(isDemoOrg)
              )}
            >
              <Building2 className="h-3.5 w-3.5 drop-shadow-sm" />
            </span>
            <span className="max-w-[150px] truncate text-sm font-medium">{orgName || "Select Organization"}</span>
            {isDemoOrg && (
              <Badge variant="warning" className="ml-0.5 text-[10px]">
                <FlaskConical className="mr-0.5 h-2.5 w-2.5" />
                Demo
              </Badge>
            )}
          </div>
          <ChevronDown className="h-4 w-4 shrink-0 text-muted-foreground transition-transform duration-200 ease-out-expo group-data-[state=open]/orgbtn:rotate-180" />
        </Button>
      </DropdownMenuTrigger>
      <DropdownMenuContent align="start" sideOffset={8} className="w-72 p-0">
        <OrganizationMenuContent
          currentOrgId={orgId}
          currentOrgName={orgName}
          currentIsDemo={isDemoOrg}
          memberships={memberships}
          isLyfeAdmin={isLyfeAdmin}
          showCreateOption={showCreateOption}
          showManageOption={showManageOption}
          onSwitchOrg={handleSwitchOrg}
          onManageOrg={handleManageOrg}
          onCreateOrg={handleCreateOrg}
        />
      </DropdownMenuContent>
    </DropdownMenu>
  );
}

function OrganizationMenuContent({
  currentOrgId,
  currentOrgName,
  currentIsDemo,
  memberships,
  isLyfeAdmin,
  showCreateOption,
  showManageOption,
  onSwitchOrg,
  onManageOrg,
  onCreateOrg,
}: {
  currentOrgId: string | null;
  currentOrgName: string | null;
  currentIsDemo: boolean;
  memberships: Array<{
    organization: {
      id: string;
      name: string;
      imageUrl?: string;
      publicMetadata?: Record<string, unknown>;
    };
  }>;
  isLyfeAdmin: boolean;
  showCreateOption: boolean;
  showManageOption: boolean;
  onSwitchOrg: (orgId: string) => void;
  onManageOrg: () => void;
  onCreateOrg: () => void;
}) {
  const orgCount = memberships.length;

  return (
    <>
      {/* Current org header card */}
      <div className="relative overflow-hidden rounded-t-md border-b border-border/60 bg-gradient-to-br from-card via-card to-accent/50 px-3 py-3">
        <div
          aria-hidden
          className="pointer-events-none absolute -right-10 -top-10 h-24 w-24 rounded-full bg-gradient-to-br from-[hsl(217_91%_60%/0.14)] to-transparent blur-2xl"
        />
        <div className="relative flex items-center gap-2.5">
          <div
            className={cn(
              "flex h-9 w-9 shrink-0 items-center justify-center rounded-lg bg-gradient-to-br text-white shadow-sm ring-1 ring-white/10",
              gradientForOrg(currentIsDemo)
            )}
          >
            <Building2 className="h-4 w-4 drop-shadow-sm" />
          </div>
          <div className="min-w-0 flex-1">
            <p className="text-[10px] font-semibold uppercase tracking-wider text-muted-foreground">
              Current Organization
            </p>
            <p className="truncate text-sm font-semibold tracking-tight text-foreground">
              {currentOrgName || "No organization"}
            </p>
          </div>
          {currentIsDemo && (
            <Badge variant="warning" className="shrink-0 text-[10px]">
              <FlaskConical className="mr-0.5 h-2.5 w-2.5" />
              Demo
            </Badge>
          )}
        </div>
      </div>

      {/* Org list */}
      <div className="px-1.5 py-1.5">
        <div className="mb-1 flex items-center justify-between px-2 pt-1">
          <p className="text-[10px] font-semibold uppercase tracking-wider text-muted-foreground">Switch to</p>
          <p className="text-[10px] tabular-nums text-muted-foreground">
            {orgCount} {orgCount === 1 ? "org" : "orgs"}
          </p>
        </div>

        {memberships.length === 0 ? (
          <div className="rounded-md border border-dashed border-border/60 bg-muted/30 px-3 py-6 text-center">
            <Building2 className="mx-auto h-6 w-6 text-muted-foreground/50" />
            <p className="mt-2 text-xs text-muted-foreground">No organizations available</p>
          </div>
        ) : (
          <div className="space-y-0.5">
            {memberships.map(({ organization: org }) => {
              const metadata = (org.publicMetadata || {}) as unknown as OrganizationMetadata;
              const isDemoOrg = metadata?.type === "DEMO";
              const isActive = org.id === currentOrgId;

              return (
                <DropdownMenuItem
                  key={org.id}
                  onClick={() => onSwitchOrg(org.id)}
                  className={cn(
                    "group/orgitem relative flex cursor-pointer items-center gap-2.5 rounded-md px-2.5 py-2 transition-colors",
                    isActive && "bg-accent/60"
                  )}
                >
                  {/* Active edge accent */}
                  {isActive && (
                    <span
                      aria-hidden
                      className="absolute left-0 top-1/2 h-6 w-[3px] -translate-y-1/2 rounded-r-full bg-gradient-to-b from-[hsl(217_91%_60%)] to-[hsl(225_78%_46%)]"
                    />
                  )}

                  <div
                    className={cn(
                      "flex h-8 w-8 shrink-0 items-center justify-center rounded-md bg-gradient-to-br shadow-xs ring-1 ring-white/10 transition-transform duration-200 ease-out-expo group-hover/orgitem:scale-105",
                      gradientForOrg(isDemoOrg)
                    )}
                  >
                    {org.imageUrl ? (
                      <Image src={org.imageUrl} alt={org.name} width={20} height={20} className="rounded" />
                    ) : (
                      <Building2 className="h-4 w-4 text-white drop-shadow-sm" />
                    )}
                  </div>

                  <div className="min-w-0 flex-1">
                    <div className="flex items-center gap-1.5">
                      <span className={cn("truncate text-sm", isActive ? "font-semibold" : "font-medium")}>
                        {org.name}
                      </span>
                      {isDemoOrg && <FlaskConical className="h-3 w-3 shrink-0 text-healthcare-warning" />}
                    </div>
                    <p className="truncate text-[10px] uppercase tracking-wider text-muted-foreground">
                      {isDemoOrg ? "Sandbox" : "Production"}
                    </p>
                  </div>

                  {isActive ? (
                    <Check className="h-4 w-4 shrink-0 text-[hsl(217_91%_45%)]" />
                  ) : (
                    <span className="h-4 w-4 shrink-0" aria-hidden />
                  )}
                </DropdownMenuItem>
              );
            })}
          </div>
        )}
      </div>

      {/* Action footer */}
      {(showManageOption || (showCreateOption && isLyfeAdmin)) && (
        <>
          <DropdownMenuSeparator className="my-0" />
          <div className="space-y-0.5 bg-muted/20 px-1.5 py-1.5">
            {showManageOption && (
              <DropdownMenuItem
                onClick={onManageOrg}
                className="group/manage cursor-pointer rounded-md px-2.5 py-2 text-sm transition-colors"
              >
                <span className="mr-2 flex h-6 w-6 items-center justify-center rounded-md bg-muted text-muted-foreground transition-colors group-hover/manage:bg-accent group-hover/manage:text-foreground">
                  <Settings className="h-3.5 w-3.5" />
                </span>
                <span className="flex-1 font-medium">Manage Organization</span>
              </DropdownMenuItem>
            )}

            {showCreateOption && isLyfeAdmin && (
              <DropdownMenuItem
                onClick={onCreateOrg}
                className="group/create cursor-pointer rounded-md px-2.5 py-2 text-sm transition-colors"
              >
                <span className="mr-2 flex h-6 w-6 items-center justify-center rounded-md bg-gradient-to-br from-[hsl(217_91%_60%)] to-[hsl(225_78%_46%)] text-white shadow-xs ring-1 ring-white/10">
                  <Plus className="h-3.5 w-3.5" />
                </span>
                <span className="flex-1 font-medium">Create Organization</span>
              </DropdownMenuItem>
            )}
          </div>
        </>
      )}
    </>
  );
}

export default OrganizationSwitcher;
